import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import EditIcon from "@material-ui/icons/Edit";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import moment from "moment";
import InputMask from "react-input-mask";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CardFieldInput from "../../components/CardFieldInput";
import BoardCardModal from "../../components/BoardCardModal";
import BoardSettingsModal from "../../components/BoardSettingsModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Nome do board que serve de armazenamento pra esta tela (ver
// backend/project_voicechat_status.md 2026-11-03) - resolvido por NOME
// em vez de id fixo porque o app e' revendido como SaaS: cada empresa
// tem (ou cria) o proprio board "Clientes Críticos", um id fixo so'
// funcionaria pra uma unica empresa.
const BOARD_NAME = "Clientes Críticos";

const PRIORITY_STYLE = {
  "Crítica": { bg: "#fbe9e7", color: "#c1443a" },
  "Alta": { bg: "#fdf3e3", color: "#b7791f" },
  "Média": { bg: "#eceef1", color: "#5b6472" },
  "Baixa": { bg: "#e6f4ee", color: "#1f8a5f" },
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    padding: theme.spacing(1, 1, 2),
    alignItems: "center",
  },
  cell: {
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusSelect: {
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 1),
    },
  },
  inlineInput: {
    minWidth: 110,
    "& .MuiInputBase-input": {
      fontSize: 13,
    },
  },
  inlineRelato: {
    minWidth: 180,
    maxWidth: 240,
    "& .MuiInputBase-root": {
      maxHeight: 60,
      overflowY: "auto",
    },
    "& .MuiInputBase-input": {
      fontSize: 13,
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
}));

// Status e' um campo nativo (fase do card) - os demais vem de
// CustomFieldDefinition (name -> valor), resolvido por nome em vez de
// posicao fixa pra sobreviver a admin reordenando/adicionando campos
// depois (pedido do usuario: "deixe uma forma pra eu criar mais
// colunas" - CustomFieldDefinition ja e' esse mecanismo, editavel via o
// icone de engrenagem desta tela, sem precisar de codigo novo).
const FIELD_NAMES = {
  cnpj: "CNPJ",
  sistema: "Sistema",
  ramo: "Ramo de atividade",
  contato: "Contato da loja",
  telefone: "Telefone / WhatsApp",
  relato: "Problema / relato do cliente",
  prioridade: "Prioridade",
  conclusao: "Data de conclusão",
};

const MASKS = { phone: "(99) 99999-9999", cnpj: "99.999.999/9999-99" };

// Celula de tabela editavel direto, sem precisar abrir o card - pedido
// explicito do usuario ("tem colunas na tabela que nao existe campo pra
// preencher": so Status/Prioridade eram clicaveis ali, o resto era so
// texto estatico, obrigando a abrir o modal ate pra preencher o
// telefone). Comita no blur (texto) ou na propria mudanca (data), nunca
// a cada tecla - senao uma frase digitada vira uma requisicao por letra.
const InlineTextCell = ({ value, onCommit, type, maskType, multiline, className }) => {
  const [local, setLocal] = useState(value || "");
  useEffect(() => {
    setLocal(value || "");
  }, [value]);

  const commit = () => {
    if (local !== (value || "")) onCommit(local);
  };

  if (type === "date") {
    return (
      <TextField
        type="date"
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          onCommit(e.target.value);
        }}
        size="small"
        InputProps={{ disableUnderline: true }}
        className={className}
      />
    );
  }

  const mask = MASKS[maskType];
  if (mask) {
    return (
      <InputMask mask={mask} maskChar={null} value={local} onChange={(e) => setLocal(e.target.value)} onBlur={commit}>
        {(inputProps) => (
          <TextField {...inputProps} size="small" InputProps={{ disableUnderline: true }} className={className} />
        )}
      </InputMask>
    );
  }

  return (
    <TextField
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      multiline={multiline}
      size="small"
      InputProps={{ disableUnderline: true }}
      className={className}
    />
  );
};

const ClientesCriticos = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [boardMissing, setBoardMissing] = useState(false);
  const [board, setBoard] = useState(null);
  const [phases, setPhases] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPhaseId, setNewPhaseId] = useState("");
  const [newResponsavelId, setNewResponsavelId] = useState("");
  const [newFieldValues, setNewFieldValues] = useState({});
  const [saving, setSaving] = useState(false);

  const lastWarnedCnpjRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: boardsList } = await api.get("/boards");
      const found = boardsList.find((b) => b.name === BOARD_NAME);
      if (!found) {
        setBoardMissing(true);
        setLoading(false);
        return;
      }
      setBoardMissing(false);
      const { data: detail } = await api.get(`/boards/${found.id}`);
      setBoard(detail);
      setPhases(detail.phases || []);
      setCustomFieldDefinitions(detail.customFieldDefinitions || []);
      const { data: cardsData } = await api.get("/cards", { params: { boardId: found.id } });
      setCards(cardsData);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, []);

  // Tutorial abre sozinho na primeira vez que o usuario entra nesta
  // tela (por navegador, nao por usuario logado - localStorage e'
  // simples e suficiente aqui, sem precisar de uma coluna nova no
  // banco so' pra isso). Depois disso so abre pelo botao "?" no
  // cabecalho. Pedido do usuario: "crie um tutorial na pagina pra
  // ensinar o usuario a usar".
  const TUTORIAL_SEEN_KEY = "voicechat-clientes-criticos-tutorial-seen";
  useEffect(() => {
    try {
      if (!localStorage.getItem(TUTORIAL_SEEN_KEY)) {
        setTutorialOpen(true);
        localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
      }
    } catch (e) {
      // localStorage indisponivel - sem tutorial automatico, mas o
      // botao "?" continua funcionando normalmente.
    }
  }, []);

  const fieldDef = useCallback(
    (key) => customFieldDefinitions.find((d) => d.name === FIELD_NAMES[key]),
    [customFieldDefinitions]
  );

  const fieldValue = (card, key) => {
    const def = fieldDef(key);
    if (!def) return "";
    const fv = (card.fieldValues || []).find((f) => f.customFieldDefinitionId === def.id);
    return fv?.value || "";
  };

  const phaseById = (id) => phases.find((p) => p.id === id);

  const filteredCards = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cards.filter((c) => {
      if (statusFilter && c.phaseId !== statusFilter) return false;
      const prioridade = fieldValue(c, "prioridade");
      if (priorityFilter && prioridade !== priorityFilter) return false;
      if (term) {
        const cnpj = fieldValue(c, "cnpj");
        const haystack = `${c.title || ""} ${cnpj}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [cards, search, statusFilter, priorityFilter, customFieldDefinitions]);

  // Status muda direto na linha (Select), igual coluna de dropdown da
  // planilha - nao precisa abrir o card pra isso, so' pros outros campos.
  const handleInlineStatusChange = async (card, targetPhaseId) => {
    try {
      const targetCount = cards.filter((c) => c.phaseId === targetPhaseId).length;
      await api.put(`/cards/${card.id}/move`, { targetPhaseId, targetPosition: targetCount });
      setCards((prev) =>
        prev.map((c) => (c.id === card.id ? { ...c, phaseId: targetPhaseId } : c))
      );
    } catch (err) {
      toastError(err);
    }
  };

  // Campo nativo do Card (titulo, responsavel) - PUT /cards/:id.
  const commitNativeField = async (card, patch) => {
    try {
      await api.put(`/cards/${card.id}`, patch);
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, ...patch } : c)));
    } catch (err) {
      toastError(err);
    }
  };

  // Campo personalizado (CNPJ, Sistema, etc) - POST /card-field-values
  // (upsert). Usado por toda celula editavel da tabela que nao e' nativa
  // do Card nem o Status (que move de fase, ver handleInlineStatusChange
  // acima).
  const commitCustomField = async (card, key, value) => {
    const def = fieldDef(key);
    if (!def) return;
    try {
      await api.post("/card-field-values", { cardId: card.id, customFieldDefinitionId: def.id, value });
      setCards((prev) =>
        prev.map((c) => {
          if (c.id !== card.id) return c;
          const others = (c.fieldValues || []).filter((f) => f.customFieldDefinitionId !== def.id);
          return { ...c, fieldValues: [...others, { customFieldDefinitionId: def.id, value }] };
        })
      );
      if (key === "cnpj") checkCnpjDuplicate(def, value);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRowClick = (card) => {
    setSelectedCard(card);
    setCardModalOpen(true);
  };

  const resetAddForm = () => {
    setAddOpen(false);
    setNewTitle("");
    setNewPhaseId("");
    setNewResponsavelId("");
    setNewFieldValues({});
    lastWarnedCnpjRef.current = null;
  };

  const checkCnpjDuplicate = (def, rawValue) => {
    if (def.type !== "cnpj") return;
    const digits = (rawValue || "").replace(/\D/g, "");
    if (digits.length < 14 || digits === lastWarnedCnpjRef.current) return;
    const match = cards.find((c) =>
      (c.fieldValues || []).some(
        (fv) => fv.customFieldDefinitionId === def.id && (fv.value || "").replace(/\D/g, "") === digits
      )
    );
    if (match) {
      lastWarnedCnpjRef.current = digits;
      toast.warn(`Já existe um caso para este CNPJ: "${match.title || "sem título"}".`);
    }
  };

  const handleNewFieldChange = (defId, value) => {
    setNewFieldValues((prev) => ({ ...prev, [defId]: value }));
    const def = customFieldDefinitions.find((d) => d.id === defId);
    if (def) checkCnpjDuplicate(def, value);
  };

  const handleOpenAdd = () => {
    setNewResponsavelId(user?.id || "");
    setNewPhaseId(phases[0]?.id || "");
    setAddOpen(true);
  };

  const handleSaveNew = async () => {
    if (saving || !newTitle.trim() || !newPhaseId) return;
    setSaving(true);
    try {
      const { data: newCard } = await api.post("/cards", {
        boardId: board.id,
        phaseId: newPhaseId,
        title: newTitle.trim(),
        responsavelId: newResponsavelId || null,
      });

      const filled = customFieldDefinitions.filter((def) => newFieldValues[def.id]);
      if (filled.length > 0) {
        await Promise.all(
          filled.map((def) =>
            api.post("/card-field-values", {
              cardId: newCard.id,
              customFieldDefinitionId: def.id,
              value: newFieldValues[def.id],
            })
          )
        );
      }

      toast.success("Caso adicionado!");
      resetAddForm();
      fetchAll();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");

  if (boardMissing && !loading) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Clientes Críticos</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.emptyState}>
            <ReportProblemOutlinedIcon fontSize="large" color="disabled" />
            <Typography variant="body2">
              Nenhum board chamado "{BOARD_NAME}" encontrado para esta empresa ainda.
            </Typography>
            <Typography variant="caption">
              Peça para um administrador criar um board com esse nome em Produtividade → Kanban.
            </Typography>
          </div>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Clientes Críticos</Title>
        <MainHeaderButtonsWrapper>
          <IconButton size="small" onClick={() => setTutorialOpen(true)} title="Como usar esta tela">
            <HelpOutlineIcon />
          </IconButton>
          {board && (
            <IconButton size="small" onClick={() => setSettingsOpen(true)} title="Gerenciar campos">
              <SettingsOutlinedIcon />
            </IconButton>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            disabled={!board}
          >
            Novo caso
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.filters}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Razão social ou CNPJ…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          variant="outlined"
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ minWidth: 170 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Todos</MenuItem>
          {phases.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          variant="outlined"
          select
          label="Prioridade"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          style={{ minWidth: 150 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Todas</MenuItem>
          {(fieldDef("prioridade")?.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary">
          {filteredCards.length} caso(s)
        </Typography>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Data do registro</TableCell>
              <TableCell>Registrado por</TableCell>
              <TableCell>Razão social</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell>Sistema</TableCell>
              <TableCell>Ramo de atividade</TableCell>
              <TableCell>Contato da loja</TableCell>
              <TableCell>Telefone / WhatsApp</TableCell>
              <TableCell>Problema / relato</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data de conclusão</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={13} />}
            {!loading &&
              filteredCards.map((card) => {
                const phase = phaseById(card.phaseId);
                const prioridade = fieldValue(card, "prioridade");
                const pStyle = PRIORITY_STYLE[prioridade];
                const relato = fieldValue(card, "relato");
                return (
                  <TableRow key={card.id} hover>
                    <TableCell className={classes.cell}>{formatDate(card.createdAt)}</TableCell>
                    <TableCell>
                      <Select
                        className={classes.statusSelect}
                        value={card.responsavelId || ""}
                        displayEmpty
                        disableUnderline
                        onChange={(e) => commitNativeField(card, { responsavelId: e.target.value || null })}
                        renderValue={(v) =>
                          v ? users.find((u) => u.id === v)?.name || "—" : (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>Sem responsável</em>
                        </MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        value={card.title}
                        onCommit={(v) => commitNativeField(card, { title: v })}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        maskType="cnpj"
                        value={fieldValue(card, "cnpj")}
                        onCommit={(v) => commitCustomField(card, "cnpj", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        className={classes.statusSelect}
                        value={fieldValue(card, "sistema") || ""}
                        displayEmpty
                        disableUnderline
                        onChange={(e) => commitCustomField(card, "sistema", e.target.value)}
                        renderValue={(v) =>
                          v || <Typography variant="caption" color="textSecondary">—</Typography>
                        }
                      >
                        <MenuItem value="">
                          <em>—</em>
                        </MenuItem>
                        {(fieldDef("sistema")?.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        value={fieldValue(card, "ramo")}
                        onCommit={(v) => commitCustomField(card, "ramo", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        value={fieldValue(card, "contato")}
                        onCommit={(v) => commitCustomField(card, "contato", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        maskType="phone"
                        value={fieldValue(card, "telefone")}
                        onCommit={(v) => commitCustomField(card, "telefone", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineRelato}
                        multiline
                        value={relato}
                        onCommit={(v) => commitCustomField(card, "relato", v)}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        className={classes.statusSelect}
                        value={prioridade || ""}
                        displayEmpty
                        disableUnderline
                        onChange={(e) => commitCustomField(card, "prioridade", e.target.value)}
                        renderValue={(v) =>
                          v ? (
                            <Chip
                              size="small"
                              label={v}
                              style={{
                                backgroundColor: pStyle?.bg || "#eceef1",
                                color: pStyle?.color || "#5b6472",
                                fontWeight: 700,
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              —
                            </Typography>
                          )
                        }
                      >
                        {(fieldDef("prioridade")?.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        className={classes.statusSelect}
                        value={card.phaseId || ""}
                        disableUnderline
                        onChange={(e) => handleInlineStatusChange(card, e.target.value)}
                        renderValue={() => (
                          <Chip
                            size="small"
                            label={phase?.name || "—"}
                            style={{
                              backgroundColor: `${phase?.color || "#5b6472"}22`,
                              color: phase?.color || "#5b6472",
                              fontWeight: 700,
                            }}
                          />
                        )}
                      >
                        {phases.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <InlineTextCell
                        className={classes.inlineInput}
                        type="date"
                        value={fieldValue(card, "conclusao")}
                        onCommit={(v) => commitCustomField(card, "conclusao", v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver / editar caso">
                        <IconButton size="small" onClick={() => handleRowClick(card)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!loading && filteredCards.length === 0 && (
              <TableRow>
                <TableCell colSpan={13}>
                  <div className={classes.emptyState}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhum caso encontrado com esses filtros.
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {board && (
        <BoardSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          boardId={board.id}
          onChanged={fetchAll}
        />
      )}

      {selectedCard && (
        <BoardCardModal
          open={cardModalOpen}
          onClose={() => setCardModalOpen(false)}
          card={selectedCard}
          customFieldDefinitions={customFieldDefinitions}
          phases={phases}
          onSaved={() => {
            fetchAll();
          }}
        />
      )}

      <Dialog open={addOpen} onClose={resetAddForm} maxWidth="xs" fullWidth>
        <DialogTitle>Novo caso crítico</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Razão social"
            fullWidth
            margin="dense"
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <TextField
            label="Status"
            select
            fullWidth
            margin="dense"
            variant="outlined"
            value={newPhaseId}
            onChange={(e) => setNewPhaseId(e.target.value)}
          >
            {phases.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Registrado por"
            select
            fullWidth
            margin="dense"
            variant="outlined"
            value={newResponsavelId}
            onChange={(e) => setNewResponsavelId(e.target.value)}
          >
            <MenuItem value="">
              <em>Sem responsável</em>
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
          {customFieldDefinitions.map((def) => (
            <CardFieldInput
              key={def.id}
              def={def}
              value={newFieldValues[def.id]}
              onChange={(value) => handleNewFieldChange(def.id, value)}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAddForm} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSaveNew}
            disabled={saving || !newTitle.trim() || !newPhaseId}
          >
            {saving ? <CircularProgress size={20} /> : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Como usar o Clientes Críticos</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Essa tela substitui a planilha de acompanhamento de clientes críticos. Funciona como uma
            tabela normal — clique em qualquer célula pra editar, sem precisar abrir nada.
          </Typography>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>1. Registrar um caso novo</Typography>
            <Typography variant="body2" color="textSecondary">
              Clique em <b>"+ Novo caso"</b>. Antes de confirmar, confira se já existe um caso pra esse
              CNPJ — se existir, um aviso aparece na hora (evita duplicar o mesmo problema).
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>2. Editar direto na tabela</Typography>
            <Typography variant="body2" color="textSecondary">
              Razão social, CNPJ, Sistema, Ramo, Contato, Telefone, Relato e Data de conclusão: clique
              na célula e digite ou escolha — salva sozinho ao sair do campo. Prioridade, Status e
              Responsável já são seletores coloridos, direto na linha.
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>3. Prioridade</Typography>
            <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 8 }}>
              {Object.entries(PRIORITY_STYLE).map(([label, style]) => (
                <Chip key={label} size="small" label={label} style={{ backgroundColor: style.bg, color: style.color, fontWeight: 700 }} />
              ))}
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Crítica: operação parada ou cliente querendo cancelar. Alta: problema recorrente ou forte
              insatisfação. Média: dificuldade sem paralisação. Baixa: orientação preventiva.
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>4. Status</Typography>
            <Typography variant="body2" color="textSecondary">
              Vai de <b>Novo</b> até <b>Resolvido</b> ou <b>Cancelado</b>, passando por Em análise, Em
              atendimento, Aguardando cliente/fornecedor e Em monitoramento. Mude direto no seletor da
              linha conforme o caso avança.
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>5. Acompanhamento do caso (responsável, próxima ação, prazo)</Typography>
            <Typography variant="body2" color="textSecondary">
              Clique no lápis (coluna Ações) pra abrir o caso completo — lá dá pra anexar arquivo, ver
              o histórico e criar <b>Tarefas</b> (próxima ação + responsável + prazo), que também
              aparecem na tela "Tarefas" do menu.
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>6. Criar mais colunas</Typography>
            <Typography variant="body2" color="textSecondary">
              Clique no ícone de engrenagem no topo da tela — em "Campos Personalizados" dá pra
              adicionar, editar ou remover colunas sem precisar pedir isso pra mim.
            </Typography>
          </Box>

          <Divider style={{ margin: "16px 0" }} />
          <Typography variant="caption" color="textSecondary">
            Criei um caso de exemplo ("Mercado Modelo Ltda — exemplo, pode excluir") já preenchido pra
            você ver como fica. Pode apagar quando quiser.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={() => setTutorialOpen(false)}>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ClientesCriticos;
